/**
 * MifeLife v2 — Pricing. Free / Pro ₺199 / Business ₺999.
 * iyzico checkout reuse on Pro & Business (existing /api/sales/subscription/checkout).
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, getToken } from '../../lib/utils';

const PLANS = [
  {
    slug: 'free', name: 'Free', price: 0,
    desc: 'Platform tamamen ücretsiz. Ödeme sadece mentör seansı için.',
    features: [
      '1 AI Yaşam Analizi · 1 Şirket Taraması',
      'AI Asistan (günlük 5 mesaj)',
      'Dashboard + Temel takvim',
      'Mentör arama + rezervasyon',
      '1 saatlik video görüşme (Jitsi)',
      'Komisyon dahil mentör ücretleri',
    ],
    cta: 'Ücretsiz Başla', ctaHref: '/kayit', highlight: false, badge: null,
  },
  {
    slug: 'plus', name: 'MifeLife Plus', price: 49,
    desc: 'Sınırsız AI + raporlar + öncelikli mentör erişimi.',
    features: [
      'Sınırsız AI Analiz (bireysel + şirket)',
      'Sınırsız AI Asistan',
      'PDF raporları (analiz çıktıları)',
      'Geçmiş analiz karşılaştırması',
      'Öncelikli mentör erişimi',
      'Özel profil rozeti',
    ],
    cta: "Plus'a Geç", highlight: true, badge: 'En çok seçilen',
  },
  {
    slug: 'business', name: 'Şirket Plus', price: 199,
    desc: 'Ekipler için. 5 kullanıcı + sınırsız şirket taraması.',
    features: [
      "Plus'taki her şey",
      '5 kullanıcı (ekip üyesi)',
      'Sınırsız şirket taraması',
      'Ekip bazlı yol haritası',
      'Toplu raporlar',
      'API erişimi (yakında)',
    ],
    cta: "Şirket Plus'a Geç", highlight: false, badge: null,
  },
];

export default function V2PricingPage() {
  const nav = useNavigate();
  const [pendingSlug, setPendingSlug] = useState(null);

  const select = async (p) => {
    if (p.slug === 'free') { nav('/kayit'); return; }
    if (!getToken()) { nav(`/kayit?after=pricing&plan=${p.slug}`); return; }
    setPendingSlug(p.slug);
    try {
      // Repurpose existing checkout — defaults to starter/pro tier mapping.
      const tier = p.slug === 'business' ? 'pro' : 'pro';  // both paid → iyzico Pro for v2 launch
      const res = await apiRequest('/sales/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier, currency: 'TRY' }),
      });
      if (res?.redirect_url) window.location.assign(res.redirect_url);
    } catch (e) {
      console.error(e);
      alert('Ödeme başlatılamadı. Lütfen tekrar dene.');
    } finally {
      setPendingSlug(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-pricing-page">
      <V2Nav lang="tr" />
      <section className="pt-28 sm:pt-32 pb-8 text-center">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
            <Sparkles className="w-3 h-3" /> Fiyatlandırma
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight">Platform ücretsiz. Sadece mentörlere öde.</h1>
          <p className="mt-4 text-base text-slate-400">AI analiz + mentör eşleştirme + video görüşme — hepsi ücretsiz. Plus seninle büyür: sınırsız analiz, raporlar, öncelik. İptal her zaman serbest.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((p) => (
            <div key={p.slug} className={`relative rounded-2xl border p-7 flex flex-col ${p.highlight ? 'border-violet-400/40 bg-gradient-to-b from-violet-500/[0.08] to-transparent shadow-2xl shadow-violet-500/10' : 'border-white/10 bg-white/[0.02]'}`} data-testid={`v2-plan-${p.slug}`}>
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-violet-500 text-[10px] uppercase tracking-[0.18em] text-white">{p.badge}</div>
              )}
              <div className="text-base font-semibold tracking-tight mb-1">{p.name}</div>
              <div className="text-sm text-slate-400 mb-6 min-h-[40px]">{p.desc}</div>
              <div className="mb-6">
                <span className="text-4xl font-semibold tabular-nums">₺{p.price}</span>
                {p.price > 0 && <span className="text-sm text-slate-500 ml-1">/ ay</span>}
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2.5 text-[13px] text-slate-200 leading-relaxed">
                    <Check className="w-3.5 h-3.5 text-violet-300 mt-0.5 flex-shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.slug === 'free' ? (
                <Link to="/kayit" className={`inline-flex items-center justify-center h-11 rounded-lg text-sm font-semibold transition ${p.highlight ? 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white' : 'border border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]'}`} data-testid={`v2-plan-cta-${p.slug}`}>
                  {p.cta}
                </Link>
              ) : (
                <button onClick={() => select(p)} disabled={pendingSlug === p.slug} className={`inline-flex items-center justify-center h-11 rounded-lg text-sm font-semibold transition disabled:opacity-50 ${p.highlight ? 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white' : 'border border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]'}`} data-testid={`v2-plan-cta-${p.slug}`}>
                  {pendingSlug === p.slug ? 'Yönlendiriliyor…' : p.cta}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-12 text-center text-xs text-slate-500">KVKK/GDPR uyumlu · İstediğin zaman iptal · iyzico güvenli ödeme</div>
      </section>

      <V2Footer lang="tr" />
    </div>
  );
}
